import { BottomSheet, GymGlyph, Icon, useToast } from "@helmr/ui";
import { useNavigate } from "react-router-dom";
import { selectSession, type PickerEntry } from "../../api/auth";
import { isApiError } from "../../api/client";
import { useSessionSnapshot } from "../permissions";
import { useServices } from "../services";
import { strings } from "../strings";
import { mobile } from "./strings";
import { handOverSentinel, useSheetBack } from "./useSheetBack";
import s from "./mobile.module.css";

/**
 * The gym and branch switcher as a sheet (a listbox). Same flow as the desktop Switcher: `POST /auth/session/select`, store
 * the new token, then `queryClient.clear()` before anything renders for the new context (tenant-leak guard).
 */
export function BranchSheet({ open, onOpenChange }: { open: boolean; onOpenChange: (o: boolean) => void }) {
  const { client, session, queryClient } = useServices();
  const state = useSessionSnapshot();
  const navigate = useNavigate();
  const toast = useToast();
  useSheetBack(open, () => onOpenChange(false));

  const staffEntries = state.picker.filter((e) => e.staff);
  const current = staffEntries.find((e) => e.businessId === state.businessId);
  const branches = current?.staff?.branches ?? [];
  const others = staffEntries.filter((e) => e.businessId !== state.businessId);

  async function choose(entry: PickerEntry, branchId: string) {
    try {
      const res = await selectSession(client, { businessId: entry.businessId, branchId });
      session.setScoped({ token: res.token, expiresAt: res.expiresAt, businessId: res.businessId, branchId: res.branchId, staffId: res.staffId, roleId: res.roleId });
      queryClient.clear();
      onOpenChange(false);
      if (entry.businessId !== state.businessId) { handOverSentinel(); navigate("/home", { replace: true }); }
    } catch (err) {
      toast.show(isApiError(err) && err.isForbidden ? strings.signIn.errors.noAccess : strings.shell.switchFailed);
    }
  }

  const row = (key: string, selected: boolean, label: string, onClick: () => void, icon?: boolean) => (
    <button key={key} type="button" role="option" aria-selected={selected} className={s.opt} onClick={onClick}>
      <span className={s.logo}>{icon ? <Icon name="swap" size={16} /> : <GymGlyph />}</span>
      <span className={s.optText}><b>{label}</b></span>
      {selected ? <Icon name="check" className={s.ck} /> : null}
    </button>
  );

  return (
    <BottomSheet open={open} onOpenChange={onOpenChange} title={mobile.branch.title} description={mobile.branch.sub}>
      <div role="listbox" aria-label={strings.shell.branches} className={s.lst}>
        {branches.map((b) => row(b.id, state.branchFilter === b.id, `${current?.name} · ${b.name}`, () => {
          if (current && state.branchFilter !== b.id) void choose(current, b.id); else onOpenChange(false);
        }))}
        {branches.length > 1 ? row("all", state.branchFilter === "all", strings.shell.allBranches, () => { session.setBranchFilter("all"); onOpenChange(false); }) : null}
      </div>
      {others.length > 0 ? (
        <>
          <h3 className={s.sheetH}>{mobile.branch.switchBusiness}</h3>
          <div role="listbox" aria-label={strings.shell.switchBusiness} className={s.lst}>
            {others.flatMap((e) => (e.staff?.branches ?? []).map((b) => row(`${e.businessId}:${b.id}`, false, `${e.name} · ${b.name}`, () => void choose(e, b.id), true)))}
          </div>
        </>
      ) : null}
    </BottomSheet>
  );
}
